/**
 * Breakpoint Tokens
 *
 * Responsive layout system defining viewport ranges for
 * mobile, tablet, and desktop experiences.
 *
 * @see DS-004-breakpoints.md
 * @figma https://www.figma.com/design/NXGWL5ErhbsA0kqoybqQbg/Design-System?node-id=236-5598
 */

/**
 * Breakpoint values in pixels (min-width)
 */
export const breakpoints = {
  sm: 320,
  md: 768,
  lg: 1024,
  xl: 1440,
} as const;

/**
 * Max widths for each breakpoint range
 */
export const breakpointMax = {
  sm: 767,
  md: 1023,
  lg: 1439,
  xl: Infinity,
} as const;

/**
 * Reference widths for design (Figma artboard sizes)
 */
export const referenceWidths = {
  sm: 375,
  md: 800,
  lg: 1280,
  xl: 1440,
} as const;

/**
 * Grid configuration per breakpoint
 */
export const gridConfig = {
  sm: { columns: 4, gutter: 16, margin: 16 },
  md: { columns: 8, gutter: 24, margin: 32 },
  lg: { columns: 12, gutter: 24, margin: 40 },
  xl: { columns: 12, gutter: 24, margin: "auto" as const, maxWidth: 1192 },
} as const;

/**
 * Container max widths
 */
export const containerWidth = {
  sm: "100%",
  md: "100%",
  lg: "100%",
  xl: 1192,
} as const;

// Utility functions

/**
 * Get current breakpoint based on width
 */
export const getBreakpoint = (width: number): BreakpointKey => {
  if (width >= breakpoints.xl) return "xl";
  if (width >= breakpoints.lg) return "lg";
  if (width >= breakpoints.md) return "md";
  return "sm";
};

/**
 * Check if width matches a specific breakpoint
 */
export const matchesBreakpoint = (width: number, breakpoint: BreakpointKey): boolean => {
  return width >= breakpoints[breakpoint] && width <= breakpointMax[breakpoint];
};

/**
 * Check if width is at least a specific breakpoint
 */
export const isAtLeast = (width: number, breakpoint: BreakpointKey): boolean => {
  return width >= breakpoints[breakpoint];
};

/**
 * Get grid configuration for current width
 */
export const getGridConfig = (width: number) => {
  const breakpoint = getBreakpoint(width);
  return gridConfig[breakpoint];
};

/**
 * Media query helper for CSS-in-JS
 */
export const mediaQuery = {
  sm: `@media (min-width: ${breakpoints.sm}px)`,
  md: `@media (min-width: ${breakpoints.md}px)`,
  lg: `@media (min-width: ${breakpoints.lg}px)`,
  xl: `@media (min-width: ${breakpoints.xl}px)`,
} as const;

// Type exports
export type BreakpointKey = keyof typeof breakpoints;
export type GridConfig = (typeof gridConfig)[BreakpointKey];
